import React, { useEffect, useRef, useState } from 'react'
import { getBreadCrumbs } from '../../../../utils/utils'

import './App.scss'
import { NavigationProps } from './Navigation.types'
import { BreadCrumbs, Menu } from '../data/data.types'
import NavMenuItem from './NavMenuItem'
import { getActiveTab, getSubMenuCount, handleLinkClick } from '../utils/common-utils'
import { ANALYTICS_TYPES } from '../../../SiteNav/util'

export const Navigation = ({
  mainMenu,
  layoutClass,
  textSizeClass,
  menuIconDisable,
  rightButton,
  styles = {
    navBackgroundColor: '#F5F5F5',
    linkTextColor: '#036BB7',
    linkTextColorHover: '#00284a',
    navTabHoverBorderBottom: 'rgba(0, 40, 75, 0.5)',
    navTabHoverBackgroundColor: '#F5F5F5',
    navTabSelectedBorderBottom: 'rgb(0,40,73)',
  },
  navLayout,
  enableAnalytics,
}: NavigationProps) => {
  let breadCrumbs: BreadCrumbs | undefined = undefined
  let menuItems: Menu[] = []
  const [scrollPosition, setScrollPosition] = useState(0)
  const [isSticky, setIsSticky] = useState(false)
  const [isScrolling, setIsScrolling] = useState(false)
  const [currentFocusMenu, setCurrentFocusMenu] = useState<number | null>(null)
  const [activeTab, setActiveTab] = useState<string>(() => getActiveTab(mainMenu?.data.menuItems))
  const wrapperRef = useRef<HTMLUListElement>(null)

  const [totalNumberMenuItems] = useState<number>(() => {
    if (!mainMenu?.data?.menuItems) {
      return 0
    }
    return (
      mainMenu.data.menuItems.length +
      mainMenu.data.menuItems.reduce((prev, menu) => {
        return prev + getSubMenuCount(menu)
      }, 0) +
      (rightButton ? 1 : 0)
    )
  })
  const firstNavItemRef = useRef<HTMLAnchorElement>(null)
  const navRef = useRef<HTMLElement>(null)
  const navigationAlignment =
    navLayout === 'center' ? 'u-flexJustifyCenter' : 'u-flexJustifyBetween'
  if (mainMenu?.data?.menuItems) {
    const { data } = mainMenu
    menuItems = data.menuItems
    breadCrumbs = getBreadCrumbs(menuItems)
    if (menuItems.length) {
      const event = new Event('NavLoaded')
      document.dispatchEvent(event)
    }
  }

  const scrollListener = () => {
    const body = document.getElementsByTagName('body')[0]
    const isLargeViewport = window.innerWidth >= 1025
    const newScrollPosition = window.scrollY
    if (!isScrolling) {
      window.requestAnimationFrame(() => {
        if (isLargeViewport && getComputedStyle(body).overflowY !== 'hidden') {
          setIsSticky(
            scrollPosition > 72 && newScrollPosition <= scrollPosition && newScrollPosition > 72
          )
        }
        setScrollPosition(newScrollPosition)
        setIsScrolling(false)
      })
      setIsScrolling(true)
    }
  }

  useEffect(() => {
    const ua = window.navigator.userAgent.toLowerCase()
    // IE user-agent strings contain "MSIE" and/or "Trident" (code name for IE's rendering engine)
    const _isIE = ua.indexOf('msie') !== -1 || ua.indexOf('trident') !== -1
    if (!_isIE) {
      window.addEventListener('scroll', scrollListener)
    }
    return () => {
      window.removeEventListener('scroll', scrollListener)
    }
  }, [scrollPosition])

  useEffect(() => {
    const mouseTrap = (e: MouseEvent) => {
      const nav = navRef.current
      if (!nav || navRef.current?.contains(e.target as Node) || currentFocusMenu === null) {
        return
      }
      setCurrentFocusMenu(null)
    }
    window.addEventListener('click', mouseTrap)
    return () => {
      window.removeEventListener('click', mouseTrap)
    }
  }, [currentFocusMenu])
  const depthLevel = 0

  let rightButtonMenu: Menu | null = null
  if (rightButton) {
    rightButtonMenu = {
      HstMenuItemRepresentation: {
        name: rightButton.label,
        url: rightButton.link,
        parameters: {
          breadcrumbReverse: 'false',
          sitemapDisable: 'false',
          breadcrumbDisable: 'false',
          menuDisable: 'false',
          showDropdown: 'false',
          ['data-analytics']: rightButton.analytics,
          analyticsSatellite: rightButton.analyticsSatellite,
        },
        menuItems: [],
      },
    }
  }
  const buildBreadcrumbs = () => {
    if (!breadCrumbs) {
      return null
    }
    const dataAnalyticsValue: string[] = []
    const breadCrumbList = breadCrumbs.breadCrumbs.map(breadCrumb => {
      dataAnalyticsValue.push(breadCrumb.pageName)
      return (
        <React.Fragment key={breadCrumb.pageName}>
          <li className='u-textSizeMsrm1 List-item u-paddingVert8 u-paddingHoriz0'>
            <a
              href={breadCrumb.pageURL}
              data-analytics={dataAnalyticsValue.join(' | ')}
              data-testid={`breadcrumb-${breadCrumb.pageName}`}
              onClick={
                enableAnalytics
                  ? e => {
                      handleLinkClick(
                        e,
                        ANALYTICS_TYPES.BREADCRUMB,
                        dataAnalyticsValue.join(' | '),
                        undefined
                      )
                    }
                  : undefined
              }
              className={`u-textSizeMsrm1 u-linkClean ${
                breadCrumbs?.currentPageParams.breadcrumbReverse === 'true' && 'u-textColorWhite'
              }`}
            >
              {breadCrumb.pageName}
            </a>
          </li>
          <span
            aria-hidden='true'
            className='Icon u-textSizeMsrm1 List-item u-paddingVert8 u-paddingHoriz8'
          >
            &#xe5cc;
          </span>
        </React.Fragment>
      )
    })
    dataAnalyticsValue.push(breadCrumbs.currentPage)
    breadCrumbList.push(
      <li
        key={`breadcrumb-current-${breadCrumbs.currentPage}`}
        className='u-textSizeMsrm1 List-item u-paddingVert8 u-paddingHoriz0'
        aria-current={'page'}
      >
        <a
          className='u-textColorLight u-linkClean'
          href={breadCrumbs.currentPageUrl}
          data-testid={`breadcrumb-current-${breadCrumbs.currentPage}`}
          onClick={
            enableAnalytics
              ? e => {
                  handleLinkClick(
                    e,
                    ANALYTICS_TYPES.BREADCRUMB,
                    dataAnalyticsValue.join(' | '),
                    undefined
                  )
                }
              : undefined
          }
          data-analytics={dataAnalyticsValue.join(' | ')}
        >
          {breadCrumbs.currentPage}
        </a>
      </li>
    )
    return breadCrumbList
  }
  const isAriaCurrent = (url: string): 'false' | 'true' => {
    if (url) {
      const urlSplit = window.location.href.split('/')
      const currentUrl = urlSplit[urlSplit.length - 1]
      if (url.includes(currentUrl)) return 'true'
      return 'false'
    }
    return 'false'
  }
  useEffect(() => {
    if (mainMenu?.data?.menuItems) setActiveTab(getActiveTab(mainMenu?.data?.menuItems))
  }, [mainMenu])

  return (
    <>
      <style
        type='text/css'
        dangerouslySetInnerHTML={{
          __html: `
         .navBackground {background-color: ${styles.navBackgroundColor}}
      `,
        }}
      />
      <div
        className={`navigation-bar navBackground ${isSticky ? 'nav-sticky u-sizeFull' : ''}`}
        data-testid={'nav-parent'}
      >
        <div
          className={`${layoutClass || 'u-sizeConstrained'} u-sm-hidden u-md-hidden u-posRelative`}
          data-testid={'layout-element'}
        >
          <nav className={`u-flex ${navigationAlignment}`} ref={navRef}>
            <ul
              className='menus List List--selectable List--inline u-flexJustifyStart u-border0'
              ref={wrapperRef}
              role='menu'
            >
              {menuItems?.map((menu, index) => {
                const totalSubmenus = getSubMenuCount(menuItems[index])
                return (
                  <NavMenuItem
                    item={menu}
                    textSizeClass={textSizeClass}
                    key={menu.HstMenuItemRepresentation.name}
                    depthLevel={depthLevel}
                    index={index}
                    styles={styles}
                    activeTab={activeTab}
                    currentFocusMenu={currentFocusMenu}
                    totalSubmenus={totalSubmenus}
                    menuIconDisable={menuIconDisable}
                    firstNavItemRef={index === 0 ? firstNavItemRef : null}
                    updateCurrFocusedIndex={setCurrentFocusMenu}
                    updateActiveTab={setActiveTab}
                    isRightButton={false}
                    enableAnalytics={enableAnalytics}
                    parentRef={wrapperRef}
                  />
                )
              })}
            </ul>
            {rightButtonMenu && (
              <a
                id='nav-right-link'
                href={rightButton?.link || ''}
                data-analytics={rightButton?.analytics || ''}
                className=' u-linkClean u-flex u-flexAlignItemsCenter u-paddingVert8 right-link '
                aria-current={isAriaCurrent(rightButton?.link as string)}
                aria-expanded='false'
                aria-haspopup='false'
              >
                {rightButtonMenu?.HstMenuItemRepresentation?.name || ''}
              </a>
            )}
          </nav>
          {breadCrumbs &&
            !isSticky &&
            breadCrumbs.currentPageParams.breadcrumbDisable !== 'true' &&
            breadCrumbs.breadCrumbs.length !== 0 && (
              <nav className='breadcrumbs' aria-label='Breadcrumb' data-testid={'breadcrumb'}>
                <ul>{buildBreadcrumbs()}</ul>
              </nav>
            )}
        </div>
      </div>
    </>
  )
}

export default Navigation
